import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocales } from 'src/locales';

const DISMISS_KEY = 'pwa-install-dismissed';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

function isIos() {
  const ua = window.navigator.userAgent;
  const iPad = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  return /iphone|ipad|ipod/i.test(ua) || iPad;
}

export default function PwaInstallPrompt() {
  const { t } = useLocales();
  const isPhone = useMediaQuery('(max-width:599.95px), ((hover: none) and (pointer: coarse))', { noSsr: true });
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || window.sessionStorage.getItem(DISMISS_KEY) === '1') {
      return undefined;
    }

    const onPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);

    if (isIos()) {
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (!isPhone || !visible) {
    return null;
  }

  const dismiss = () => {
    window.sessionStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) {
      return;
    }

    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    setInstallEvent(null);

    if (choice.outcome === 'accepted') {
      setVisible(false);
    }
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        p: 2,
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle1">{t('pwa.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {installEvent ? t('pwa.text') : t('pwa.ios')}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button color="inherit" onClick={dismiss}>
            {t('pwa.later')}
          </Button>
          {installEvent ? (
            <Button variant="contained" onClick={install}>
              {t('pwa.install')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}
